<script>
// Cursor de mouse falso que desliza até o alvo do passo e dá um pulso de
// clique ao chegar. É o que transforma "leia a instrução e procure o botão"
// em "olha, é aqui".
//
// Move por `transform` num wrapper `position: fixed` — nunca por top/left.
// Transform é compositável na GPU; animar posição real dispara layout a cada
// frame, que é o mesmo jank já documentado para `v-expand-transition`.
//
// `pointer-events: none` no wrapper E no svg é load-bearing, não higiene: no
// modo híbrido o clique real do usuário precisa atravessar o cursor e chegar
// ao botão que está embaixo dele.
import { prefersReducedMotion } from '@modules/central-config/utils/motion'

// Espelha `--motion-duration-slow` (320ms). Duplicado em JS porque o
// fallback de chegada precisa do número, e ler a var computada do :root a
// cada movimento custaria um reflow.
const DURACAO_MOVIMENTO_MS = 320
// Margem sobre a duração para o fallback de chegada. `transitionend` não
// dispara em jsdom, e no browser ele silenciosamente não dispara quando a
// transição é interrompida — o que acontece a cada "Próximo" rápido.
const FOLGA_CHEGADA_MS = 40

export default {
  name: 'GuidedTourCursor',

  props: {
    // Ponto de destino em coordenadas de viewport. null → sem alvo (o cursor
    // some em vez de apontar para o canto superior esquerdo).
    point: {
      type: Object,
      default: null,
    },

    visible: {
      type: Boolean,
      default: false,
    },

    // Incrementar dispara o pulso de clique sem exigir movimento — usado
    // quando o passo troca mas o alvo é o mesmo elemento.
    clickToken: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      pulsando: false,
      // Resolvido uma vez: matchMedia não é reativo, e um computed daria a
      // falsa impressão de que acompanha a preferência em tempo real.
      semMovimento: false,
      chegadaTimer: null,
      // Invalida a chegada de um movimento superado por outro mais recente.
      chegadaToken: 0,
    }
  },

  computed: {
    ativo() {
      return this.visible && !!this.point
    },

    estiloDoCursor() {
      const x = this.point ? this.point.x : 0
      const y = this.point ? this.point.y : 0
      return {
        transform: `translate3d(${x}px, ${y}px, 0)`,
        opacity: this.ativo ? '1' : '0',
        transition: this.semMovimento
          ? 'none'
          : `transform var(--motion-duration-slow) var(--motion-easing-standard), opacity var(--motion-duration-default) linear`,
      }
    },
  },

  watch: {
    // O valor anterior vem de graça no watcher — não há rect anterior guardado
    // em lugar nenhum.
    point(novo, velho) {
      if (!novo) return
      const mesmoPonto = velho && velho.x === novo.x && velho.y === novo.y
      if (mesmoPonto) return
      this.agendarChegada()
    },

    clickToken(novo, velho) {
      if (novo === velho) return
      this.pulsar()
    },
  },

  created() {
    this.semMovimento = prefersReducedMotion()
  },

  beforeDestroy() {
    this.limparChegada()
  },

  methods: {
    // Sob reduced-motion o cursor aparece direto no alvo e não pulsa: ele
    // ainda diz "é aqui", sem nenhum movimento.
    agendarChegada() {
      this.limparChegada()
      if (this.semMovimento) return
      this.chegadaToken += 1
      const token = this.chegadaToken
      this.chegadaTimer = setTimeout(() => {
        this.chegadaTimer = null
        if (token !== this.chegadaToken) return
        this.pulsar()
      }, DURACAO_MOVIMENTO_MS + FOLGA_CHEGADA_MS)
    },

    // Também chamado pelo `transitionend` do transform — o que chegar
    // primeiro vence, e o segundo vira no-op pelo guard de `pulsando`.
    aoTerminarMovimento(evento) {
      if (evento && evento.propertyName !== 'transform') return
      this.limparChegada()
      this.pulsar()
    },

    pulsar() {
      if (this.semMovimento || this.pulsando) return
      this.pulsando = true
      this.$nextTick(() => {
        // Retirar a classe no fim da animação deixa o próximo pulso rearmado;
        // `animationend` no ripple é o sinal, com o mesmo motivo de fallback
        // do movimento — se não vier, o passo seguinte reagenda de qualquer jeito.
        setTimeout(() => {
          this.pulsando = false
        }, DURACAO_MOVIMENTO_MS)
      })
    },

    limparChegada() {
      if (this.chegadaTimer) {
        clearTimeout(this.chegadaTimer)
        this.chegadaTimer = null
      }
    },
  },
}
</script>

<template>
  <div
    class="gt-cursor"
    :class="{ 'gt-cursor--pulsando': pulsando }"
    :style="estiloDoCursor"
    aria-hidden="true"
    @transitionend="aoTerminarMovimento"
  >
    <svg
      class="gt-cursor__svg"
      width="26"
      height="26"
      viewBox="0 0 26 26"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      focusable="false"
    >
      <circle
        class="gt-cursor__ripple"
        cx="4"
        cy="4"
        r="7"
        fill="none"
        stroke="var(--primary)"
        stroke-width="2"
      />
      <g class="gt-cursor__seta">
        <polygon
          points="0,0 14,14 8,14 8,22 5,22 5,14 0,20"
          fill="var(--neutral-900)"
          stroke="var(--white)"
          stroke-width="1.5"
          stroke-linejoin="round"
        />
      </g>
    </svg>
  </div>
</template>

<style lang="sass" scoped>
.gt-cursor
  position: fixed
  top: 0
  left: 0
  // z-index entre o spotlight (260) e o balão (262): o cursor aponta por cima
  // do furo, mas nunca cobre o texto que explica o passo.
  z-index: 261
  // Sem isto o cursor falso intercepta o clique real do modo híbrido.
  pointer-events: none
  will-change: transform

  &__svg
    display: block
    pointer-events: none
    // O SVG desenha a partir do canto superior esquerdo, como um ponteiro real:
    // a ponta da seta é (0,0), então o `point` recebido é a ponta, não o centro.
    overflow: visible

  &__seta
    transform-origin: top left

  &__ripple
    opacity: 0
    transform-origin: 4px 4px

  &--pulsando
    .gt-cursor__seta
      animation: gtCursorClique var(--motion-duration-slow) var(--motion-easing-standard)

    .gt-cursor__ripple
      animation: gtCursorRipple var(--motion-duration-slow) var(--motion-easing-standard)

@keyframes gtCursorClique
  0%
    transform: scale(1)
  45%
    transform: scale(0.82)
  100%
    transform: scale(1)

@keyframes gtCursorRipple
  0%
    opacity: 0.9
    transform: scale(0.4)
  100%
    opacity: 0
    transform: scale(1.8)

// O gate real de acessibilidade é o CSS; o JS só encurta os timers.
@media (prefers-reduced-motion: reduce)
  .gt-cursor
    transition: none !important

    .gt-cursor__seta,
    .gt-cursor__ripple
      animation: none !important
</style>
